import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Copy, Clock, CheckCircle, XCircle, FileText, Terminal, Image, ChevronDown, Trash2, Play, Loader2, Edit3, Info, RefreshCw, Code, Link, Wrench } from 'lucide-react';
import { useClerk, useOrganization } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './ReportExecutionModal.css';

function ReportExecutionModal({ isOpen, onClose, executionResult, reportName, reportId, selectedWorkspace, onDelete, onEdit, onRun, jobId, reportStatus, progressData }) {
  const [copiedStates, setCopiedStates] = useState({});
  const [runHistory, setRunHistory] = useState([]);
  const [, setShowRunHistory] = useState(false);
  const [selectedRunTimestamp, setSelectedRunTimestamp] = useState(null);
  const [historicalResult, setHistoricalResult] = useState(null);
  const [, setHistoryLoading] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    stdout: false,
    stderr: false,
    saveLog: true  // Show save log expanded by default
  });
  const [saveLogContent, setSaveLogContent] = useState('');
  const [isSavePending, setIsSavePending] = useState(reportStatus === 'pending');
  const [activeTab, setActiveTab] = useState(''); // Will be set dynamically to first available tab
  const [viewedTabs, setViewedTabs] = useState(new Set()); // Track which tabs have been viewed
  const [latestRunTimestamp, setLatestRunTimestamp] = useState(null); // Track the latest run to show unread markers
  const [reportExplanation, setReportExplanation] = useState('');
  const [explanationLoading, setExplanationLoading] = useState(false);
  const [explanationGenerated, setExplanationGenerated] = useState(false);
  const [showApiDropdown, setShowApiDropdown] = useState(false);
  const [isFixingError, setIsFixingError] = useState(false);
  const [fixErrorResult, setFixErrorResult] = useState(null);
  const apiDropdownRef = useRef(null);
  const clerk = useClerk();
  const { organization } = useOrganization();

  // Close API dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (apiDropdownRef.current && !apiDropdownRef.current.contains(event.target)) {
        setShowApiDropdown(false);
      }
    };

    if (showApiDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showApiDropdown]);

  // Load existing explanation from backend when modal opens
  useEffect(() => {
    if (!isOpen || !reportId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization || isSavePending) {
      return;
    }

    const loadExistingExplanation = async () => {
      try {
        const sessionToken = await clerk.session.getToken();
        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);
        const queryParams = new URLSearchParams({
          workspace_id: selectedWorkspace.workspace_id
        });

        const response = await fetch(
          `${apiBaseUrl}/saved-reports/${reportId}/explanation?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.has_explanation && data.explanation) {
            setReportExplanation(data.explanation);
            setExplanationGenerated(true);
          }
        }
      } catch (error) {
        console.error('Error loading existing explanation:', error);
      }
    };

    loadExistingExplanation();
  }, [isOpen, reportId, selectedWorkspace?.workspace_id, clerk?.session, organization, isSavePending]);

  // Use WebSocket progress data for save log updates
  useEffect(() => {
    if (!progressData || !jobId) return;

    // Update log content from accumulated log lines
    if (progressData.logLines && progressData.logLines.length > 0) {
      setSaveLogContent(progressData.logLines.join('\n'));
    }

    // Update pending status from WebSocket
    if (progressData.status === 'complete' || progressData.status === 'cancelled') {
      setIsSavePending(false);
    }
  }, [progressData, jobId]);

  // One-time REST fetch on mount as reconnection fallback
  useEffect(() => {
    if (!isOpen || !jobId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization) {
      return;
    }

    // Skip if we already have data from WebSocket
    if (progressData?.logLines?.length > 0) {
      return;
    }

    const fetchInitialState = async () => {
      try {
        const sessionToken = await clerk.session.getToken();
        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);
        const queryParams = new URLSearchParams({
          workspace_id: selectedWorkspace.workspace_id
        }).toString();

        // Fetch log
        const logResponse = await fetch(
          `${apiBaseUrl}/saved-reports/job/${jobId}/log?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (logResponse.ok) {
          const logData = await logResponse.json();
          if (logData.success && logData.log) {
            setSaveLogContent(logData.log);
          }
        }

        // Fetch status
        const statusResponse = await fetch(
          `${apiBaseUrl}/saved-reports/job/${jobId}/status?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (statusData.status === 'complete') {
            setIsSavePending(false);
          }
        }
      } catch (error) {
        console.error('Error fetching initial state:', error);
      }
    };

    fetchInitialState();
  }, [isOpen, jobId, selectedWorkspace?.workspace_id, clerk?.session, organization, progressData?.logLines?.length]);

  // Update pending status and load run history when modal opens
  useEffect(() => {
    if (isOpen && reportId && selectedWorkspace?.workspace_id && organization && clerk?.session) {
      setIsSavePending(reportStatus === 'pending');
      // Only load run history if not pending
      if (reportStatus !== 'pending') {
        loadRunHistoryAndSelectLatest();
      }
    }
  }, [isOpen, reportId, selectedWorkspace?.workspace_id, organization?.id, reportStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  // Clear historical result and explanation when report ID changes to avoid showing stale data
  useEffect(() => {
    setHistoricalResult(null);
    setRunHistory([]);
    setSelectedRunTimestamp(null);
    setSaveLogContent('');
    setReportExplanation('');
    setExplanationGenerated(false);
    setExplanationLoading(false);
    setFixErrorResult(null);
  }, [reportId]);

  // Auto-select first available tab when content changes
  useEffect(() => {
    if (!isOpen) return;

    const tabs = getAvailableTabs();
    if (tabs.length > 0) {
      const validTab = tabs.find(tab => tab.id === activeTab);
      if (!validTab) {
        setActiveTab(tabs[0].id);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, executionResult, historicalResult]);

  // Mark active tab as viewed
  useEffect(() => {
    if (activeTab && latestRunTimestamp) {
      setViewedTabs(prev => new Set([...prev, activeTab]));
    }
  }, [activeTab, latestRunTimestamp]);

  // Reset viewed tabs when viewing a different run or when a new run completes
  useEffect(() => {
    const currentTimestamp = historicalResult?.timestamp || (executionResult ? 'current' : null);

    if (currentTimestamp && currentTimestamp !== latestRunTimestamp) {
      // New run detected, reset viewed tabs
      setLatestRunTimestamp(currentTimestamp);
      setViewedTabs(new Set([activeTab])); // Mark only the currently active tab as viewed
    }
  }, [historicalResult?.timestamp, executionResult, latestRunTimestamp, activeTab]);

  const loadRunHistoryAndSelectLatest = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return;
    }

    setHistoryLoading(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id,
        limit: 10
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/runs?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.run_history && data.run_history.length > 0) {
          setRunHistory(data.run_history);

          // Auto-select the first (latest) run and load its outputs
          const latestRun = data.run_history[0];
          setSelectedRunTimestamp(latestRun.timestamp);
          await loadHistoricalRun(latestRun.timestamp);
        }
      }
    } catch (error) {
      console.error('Error loading run history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  const loadRunHistory = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return null;
    }

    setHistoryLoading(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id,
        limit: 10
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/runs?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.run_history) {
          setRunHistory(data.run_history);
          return data.run_history;
        }
      }
    } catch (error) {
      console.error('Error loading run history:', error);
    } finally {
      setHistoryLoading(false);
    }
    return null;
  };

  const loadHistoricalRun = async (timestamp) => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      console.error('Missing required data to load historical run');
      return;
    }

    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/runs/${timestamp}?${queryParams}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (!response.ok) {
        console.error(`Failed to load historical run: ${response.status} ${response.statusText}`);
        return;
      }

      const data = await response.json();
      if (data.success && data.outputs) {
        setHistoricalResult({
          outputs: data.outputs,
          timestamp: timestamp
        });
        setSelectedRunTimestamp(timestamp);
        setShowRunHistory(false);
        setFixErrorResult(null); // Clear fix result when switching runs
      } else {
        console.error('Invalid response or no outputs in historical run:', data);
      }
    } catch (error) {
      console.error('Error loading historical run:', error);
    }
  };

  const handleRunReport = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return;
    }

    setIsRunning(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/run?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      // Always reload run history and display the latest run, regardless of execution success/failure
      // The run outputs (including error.log) are still saved even when execution fails
      const updatedHistory = await loadRunHistory();

      // Always select and display the latest run (first in the history)
      if (updatedHistory && updatedHistory.length > 0) {
        const latestRun = updatedHistory[0];
        setSelectedRunTimestamp(latestRun.timestamp);
        await loadHistoricalRun(latestRun.timestamp);
      }

      if (!data.success) {
        console.error('Report execution failed:', data.detail || data.message);
      }
    } catch (error) {
      console.error('Error running report:', error);
    } finally {
      setIsRunning(false);
    }
  };

  const handleGenerateExplanation = async () => {
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId) {
      return;
    }

    setExplanationLoading(true);
    setExplanationGenerated(false);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/explanation/generate?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.explanation) {
          setReportExplanation(data.explanation);
          setExplanationGenerated(true);
        } else {
          setReportExplanation('Unable to generate explanation for this report.');
          setExplanationGenerated(true);
        }
      } else {
        setReportExplanation('Error generating explanation. Please try again later.');
        setExplanationGenerated(true);
      }
    } catch (error) {
      console.error('Error generating report explanation:', error);
      setReportExplanation('Error generating explanation. Please try again later.');
      setExplanationGenerated(true);
    } finally {
      setExplanationLoading(false);
    }
  };

  const handleFixError = async () => {
    const errorContent = getErrorContent();
    if (!clerk?.session || !organization || !selectedWorkspace?.workspace_id || !reportId || !errorContent) {
      return;
    }

    setIsFixingError(true);
    setFixErrorResult(null);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id,
        error_content: errorContent
      });

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/${reportId}/fix-error?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (data.success) {
        setFixErrorResult({
          success: true,
          message: data.response || 'Error fixed successfully. Run the report again to verify.'
        });
        // Optionally auto-run the report after fix
      } else {
        setFixErrorResult({
          success: false,
          message: data.error || data.detail || 'Failed to fix the error.'
        });
      }
    } catch (error) {
      console.error('Error fixing report:', error);
      setFixErrorResult({
        success: false,
        message: 'An error occurred while trying to fix the report.'
      });
    } finally {
      setIsFixingError(false);
    }
  };

  const formatRunTime = (timestamp) => {
    // Parse timestamp format: YYYYMMDD_HHMMSS
    const year = timestamp.substring(0, 4);
    const month = timestamp.substring(4, 6);
    const day = timestamp.substring(6, 8);
    const hour = timestamp.substring(9, 11);
    const minute = timestamp.substring(11, 13);

    // Create date object
    const runDate = new Date(year, parseInt(month) - 1, day, parseInt(hour), parseInt(minute));
    const now = new Date();

    // Format time as HH:MM
    const timeStr = runDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });

    // Calculate relative time
    const diffSeconds = Math.floor((now - runDate) / 1000);
    let relativeStr;

    if (diffSeconds < 60) {
      relativeStr = 'just now';
    } else if (diffSeconds < 3600) {
      const mins = Math.floor(diffSeconds / 60);
      relativeStr = `${mins}m ago`;
    } else if (diffSeconds < 86400) {
      const hours = Math.floor(diffSeconds / 3600);
      relativeStr = `${hours}h ago`;
    } else if (diffSeconds < 604800) {
      const days = Math.floor(diffSeconds / 86400);
      relativeStr = `${days}d ago`;
    } else {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      relativeStr = `${monthNames[runDate.getMonth()]} ${parseInt(day)}`;
    }

    return { timeStr, relativeStr };
  };

  // Generate API URL for external access
  const getExternalApiUrl = () => {
    const orgSlug = getOrgSlug(organization);
    const apiBaseUrl = getApiBaseUrl(orgSlug);
    return `${apiBaseUrl}/external/reports/run`;
  };

  // Generate curl command for copying
  const getCurlCommand = () => {
    const apiUrl = getExternalApiUrl();
    return `curl -X POST "${apiUrl}" \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"report_id": "${reportId}"}'`;
  };

  // Generate Python code for copying
  const getPythonCode = () => {
    const apiUrl = getExternalApiUrl();
    return `import requests

response = requests.post(
    "${apiUrl}",
    headers={
        "X-API-Key": "YOUR_API_KEY",
        "Content-Type": "application/json"
    },
    json={"report_id": "${reportId}"}
)
print(response.json())`;
  };

  // Generate JavaScript fetch code
  const getJavaScriptCode = () => {
    const apiUrl = getExternalApiUrl();
    return `fetch("${apiUrl}", {
  method: "POST",
  headers: {
    "X-API-Key": "YOUR_API_KEY",
    "Content-Type": "application/json"
  },
  body: JSON.stringify({ report_id: "${reportId}" })
})
  .then(res => res.json())
  .then(data => console.log(data));`;
  };

  if (!isOpen) {
    return null;
  }

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleCopy = async (content, key) => {
    try {
      await navigator.clipboard.writeText(content);
      setCopiedStates(prev => ({ ...prev, [key]: true }));
      setTimeout(() => {
        setCopiedStates(prev => ({ ...prev, [key]: false }));
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const handleDownload = (content, filename) => {
    // For images, if content is base64 or data URL, handle differently
    if (isImageFile(filename)) {
      let downloadUrl;
      if (content.startsWith('data:')) {
        // Already a data URL
        downloadUrl = content;
      } else {
        // Assume it's base64, convert to data URL
        const ext = filename.split('.').pop()?.toLowerCase();
        downloadUrl = `data:image/${ext};base64,${content}`;
      }
      
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } else {
      // Regular text file download
      const blob = new Blob([content], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    }
  };

  const formatExecutionTime = (seconds) => {
    if (!seconds || typeof seconds !== 'number') {
      return '0ms';
    }
    if (seconds < 1) {
      return `${Math.round(seconds * 1000)}ms`;
    }
    return `${seconds.toFixed(2)}s`;
  };

  const isImageFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ['png', 'jpg', 'jpeg', 'gif', 'svg', 'webp', 'bmp', 'ico'].includes(ext);
  };

  const isHtmlFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'html' || ext === 'htm';
  };

  const isJsonFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'json';
  };

  const isMarkdownFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'md' || ext === 'markdown';
  };

  const formatJsonContent = (jsonString) => {
    try {
      const parsed = JSON.parse(jsonString);
      return JSON.stringify(parsed, null, 2);
    } catch (e) {
      // If it's not valid JSON, return the original string
      return jsonString;
    }
  };

  const getFileIcon = (filename) => {
    if (!filename) return <FileText size={16} />;
    
    // Check if it's an image file first
    if (isImageFile(filename)) {
      return <Image size={16} />;
    }
    
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'json':
      case 'csv':
      case 'txt':
      case 'log':
        return <FileText size={16} />;
      case 'py':
      case 'js':
      case 'html':
      case 'css':
        return <Terminal size={16} />;
      default:
        return <FileText size={16} />;
    }
  };

  const isCSVFile = (filename) => {
    if (!filename) return false;
    const ext = filename.split('.').pop()?.toLowerCase();
    return ext === 'csv';
  };

  const parseCSV = (csvContent) => {
    const lines = csvContent.split('\n').filter(line => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };
    
    const headers = lines[0].split(',').map(header => header.trim().replace(/"/g, ''));
    const rows = lines.slice(1).map(line => 
      line.split(',').map(cell => cell.trim().replace(/"/g, ''))
    );
    
    return { headers, rows };
  };

  const getSyntaxClass = (filename) => {
    if (!filename) return 'language-text';
    const ext = filename.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'py':
        return 'language-python';
      case 'js':
        return 'language-javascript';
      case 'json':
        return 'language-json';
      case 'html':
        return 'language-html';
      case 'css':
        return 'language-css';
      case 'csv':
        return 'language-csv';
      case 'md':
      case 'markdown':
        return 'language-markdown';
      default:
        return 'language-text';
    }
  };

  const getStdoutContent = () => {
    // First check if we have stdout from the execution result
    if (executionResult?.stdout) return executionResult.stdout;

    // Otherwise look for stdout.log in outputs
    if (historicalResult?.outputs) {
      const stdoutFile = historicalResult.outputs.find(o => o.filename === 'stdout.log');
      if (stdoutFile) return stdoutFile.content;
    }

    if (executionResult?.outputs) {
      const stdoutFile = executionResult.outputs.find(o => o.filename === 'stdout.log');
      if (stdoutFile) return stdoutFile.content;
    }

    return null;
  };

  const getErrorContent = () => {
    // First check if we have stderr from the execution result
    if (executionResult?.stderr) return executionResult.stderr;

    // Otherwise look for error.log in outputs
    if (historicalResult?.outputs) {
      const errorFile = historicalResult.outputs.find(o => o.filename === 'error.log');
      if (errorFile) return errorFile.content;
    }

    if (executionResult?.outputs) {
      const errorFile = executionResult.outputs.find(o => o.filename === 'error.log');
      if (errorFile) return errorFile.content;
    }

    return null;
  };

  // Get all available tabs based on current outputs
  const getAvailableTabs = () => {
    const tabs = [];

    // Always add explanation tab as the first fixed tab (not tied to any specific run)
    if (!isSavePending) {
      tabs.push({
        id: 'explanation',
        label: 'Explanation',
        icon: <Info size={16} />,
        hasContent: true,
        isFixed: true  // Mark as fixed tab
      });
    }

    const stdoutContent = getStdoutContent();
    const errorContent = getErrorContent();
    const currentResult = historicalResult ? { outputs: historicalResult.outputs } : executionResult;
    const allOutputs = currentResult?.outputs;
    const outputFiles = allOutputs?.filter(o => o.filename !== 'error.log' && o.filename !== 'stdout.log') ?? [];

    // Add stdout tab if content exists
    if (stdoutContent) {
      tabs.push({ id: 'stdout', label: 'Standard Output', icon: <Terminal size={16} />, hasContent: true });
    }

    // Add stderr tab if content exists
    if (errorContent) {
      tabs.push({ id: 'stderr', label: 'Error Output', icon: <XCircle size={16} />, hasContent: true, isError: true });
    }

    // Add tab for each output file
    outputFiles.forEach((output, index) => {
      tabs.push({
        id: `file-${index}`,
        label: output.filename,
        icon: getFileIcon(output.filename),
        hasContent: !!output.content,
        output: output
      });
    });

    return tabs;
  };

  const renderFileContent = (output) => {
    if (!output.content) return null;

    if (isImageFile(output.filename)) {
      return (
        <div className="image-preview">
          <img
            src={output.content.startsWith('data:') ? output.content : `data:image/${output.filename.split('.').pop()?.toLowerCase()};base64,${output.content}`}
            alt={output.filename}
            className="output-image"
          />
        </div>
      );
    } else if (isHtmlFile(output.filename)) {
      return (
        <div className="html-preview">
          <iframe
            srcDoc={output.content}
            className="html-iframe"
            title={output.filename}
            sandbox="allow-scripts allow-same-origin"
          />
        </div>
      );
    } else if (isMarkdownFile(output.filename)) {
      return (
        <div className="markdown-preview">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>
            {output.content}
          </ReactMarkdown>
        </div>
      );
    } else if (isJsonFile(output.filename)) {
      return (
        <pre className={`file-content ${getSyntaxClass(output.filename)}`}>
          {formatJsonContent(output.content)}
        </pre>
      );
    } else if (isCSVFile(output.filename)) {
      return (
        <div className="csv-content">
          <div className="csv-table-container">
            {(() => {
              const { headers, rows } = parseCSV(output.content);
              return (
                <table className="csv-table">
                  <thead>
                    <tr>
                      {headers.map((header, index) => (
                        <th key={index}>{header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {row.map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </div>
      );
    } else {
      return (
        <pre className={`file-content ${getSyntaxClass(output.filename)}`}>
          {output.content}
        </pre>
      );
    }
  };

  const renderTabContent = () => {
    const stdoutContent = getStdoutContent();
    const errorContent = getErrorContent();
    const currentResult = historicalResult ? { outputs: historicalResult.outputs } : executionResult;
    const allOutputs = currentResult?.outputs;
    const outputFiles = allOutputs?.filter(o => o.filename !== 'error.log' && o.filename !== 'stdout.log') ?? [];

    // Render explanation tab
    if (activeTab === 'explanation') {
      return (
        <div className="tab-content">
          <div className="content-header">
            <div className="explanation-header">
              <Info size={18} />
              <h4>Report Explanation</h4>
            </div>
            {explanationGenerated && reportExplanation && (
              <div className="content-actions">
                <button
                  className="action-button"
                  onClick={handleGenerateExplanation}
                  disabled={explanationLoading}
                  title="Regenerate explanation"
                >
                  {explanationLoading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  <span>Regenerate</span>
                </button>
                <button
                  className="action-button"
                  onClick={() => handleCopy(reportExplanation, 'explanation')}
                  title="Copy explanation"
                >
                  {copiedStates.explanation ? <CheckCircle size={14} /> : <Copy size={14} />}
                  <span>Copy</span>
                </button>
              </div>
            )}
          </div>
          <div className="explanation-content">
            {explanationLoading ? (
              <div className="explanation-loading">
                <Loader2 size={24} className="animate-spin" />
                <p>Generating explanation...</p>
                <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                  This may take a moment...
                </span>
              </div>
            ) : explanationGenerated && reportExplanation ? (
              <div className="explanation-markdown">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {reportExplanation}
                </ReactMarkdown>
              </div>
            ) : (
              <div className="explanation-prompt">
                <Info size={48} />
                <h3>How Does This Report Work?</h3>
                <p>
                  Get an AI-generated explanation of how this report processes data, including:
                </p>
                <ul>
                  <li>What data fields, tables, and sources are accessed</li>
                  <li>What calculations, filters, and transformations are applied</li>
                  <li>How the data flows through the report</li>
                </ul>
                <button
                  className="generate-explanation-button"
                  onClick={handleGenerateExplanation}
                  disabled={explanationLoading}
                >
                  <Info size={16} />
                  <span>Generate Explanation</span>
                </button>
                <p className="explanation-note">
                  Note: Generating an explanation will use AI credits
                </p>
              </div>
            )}
          </div>
        </div>
      );
    }

    // Render stdout tab
    if (activeTab === 'stdout' && stdoutContent) {
      return (
        <div className="tab-content">
          <div className="content-header">
            <h4>Standard Output</h4>
            <div className="content-actions">
              <button
                className="action-button"
                onClick={() => handleCopy(stdoutContent, 'stdout')}
                title="Copy output"
              >
                {copiedStates.stdout ? <CheckCircle size={14} /> : <Copy size={14} />}
                <span>Copy</span>
              </button>
              <button
                className="action-button"
                onClick={() => handleDownload(stdoutContent, `${reportName}_output.txt`)}
                title="Download output"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
            </div>
          </div>
          <pre className="execution-output">
            {stdoutContent}
          </pre>
        </div>
      );
    }

    // Render stderr tab
    if (activeTab === 'stderr' && errorContent) {
      return (
        <div className="tab-content">
          <div className="content-header">
            <h4>Error Output</h4>
            <div className="content-actions">
              {selectedWorkspace?.is_owner && (
                <button
                  className="action-button fix-error-button"
                  onClick={handleFixError}
                  disabled={isFixingError}
                  title="Use AI to fix this error"
                >
                  {isFixingError ? <Loader2 size={14} className="animate-spin" /> : <Wrench size={14} />}
                  <span>{isFixingError ? 'Fixing...' : 'Fix Error'}</span>
                </button>
              )}
              <button
                className="action-button"
                onClick={() => handleCopy(errorContent, 'stderr')}
                title="Copy errors"
              >
                {copiedStates.stderr ? <CheckCircle size={14} /> : <Copy size={14} />}
                <span>Copy</span>
              </button>
              <button
                className="action-button"
                onClick={() => handleDownload(errorContent, `${reportName}_errors.txt`)}
                title="Download errors"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
            </div>
          </div>
          {fixErrorResult && (
            <div className={`fix-error-result ${fixErrorResult.success ? 'success' : 'error'}`}>
              <div className="fix-error-result-header">
                {fixErrorResult.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                <span>{fixErrorResult.success ? 'Fix Applied' : 'Fix Failed'}</span>
              </div>
              <div className="fix-error-result-message">
                {fixErrorResult.message}
              </div>
              {fixErrorResult.success && (
                <button
                  className="action-button run-after-fix"
                  onClick={handleRunReport}
                  disabled={isRunning}
                >
                  {isRunning ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  <span>Run Again</span>
                </button>
              )}
            </div>
          )}
          <pre className="execution-output error-output">
            {errorContent}
          </pre>
        </div>
      );
    }

    // Render file tab
    const fileIndex = parseInt(activeTab.replace('file-', ''));
    if (!isNaN(fileIndex) && outputFiles[fileIndex]) {
      const output = outputFiles[fileIndex];
      return (
        <div className="tab-content">
          <div className="content-header">
            <div className="file-info">
              {getFileIcon(output.filename)}
              <h4>{output.filename}</h4>
              <span className="file-size">
                {output.content ? `${output.content.length} chars` : 'Empty'}
              </span>
            </div>
            <div className="content-actions">
              <button
                className="action-button"
                onClick={() => handleCopy(output.content || '', `output-${fileIndex}`)}
                title="Copy file content"
              >
                {copiedStates[`output-${fileIndex}`] ? <CheckCircle size={14} /> : <Copy size={14} />}
                <span>Copy</span>
              </button>
              <button
                className="action-button"
                onClick={() => handleDownload(output.content || '', output.filename)}
                title="Download file"
              >
                <Download size={14} />
                <span>Download</span>
              </button>
            </div>
          </div>
          {renderFileContent(output)}
        </div>
      );
    }

    return null;
  };

  const renderContent = () => {
    // If no execution result and no historical result, show placeholder
    if (!executionResult && !historicalResult) {
      return (
        <div className="execution-content">
          <div className="no-outputs">
            <Terminal size={32} />
            <p>No execution yet</p>
            <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              Click the Run button to execute this report
            </span>
          </div>
        </div>
      );
    }

    const availableTabs = getAvailableTabs();

    // If no tabs available, show no outputs message
    if (availableTabs.length === 0) {
      return (
        <div className="execution-content">
          <div className="no-outputs">
            <FileText size={32} />
            <p>No outputs generated</p>
          </div>
        </div>
      );
    }

    return (
      <div className="execution-content">
        {/* Tabs Bar */}
        <div className="execution-tabs-container">
          <div className="execution-tabs">
            {availableTabs.map((tab) => {
              const isUnread = latestRunTimestamp && !viewedTabs.has(tab.id) && !tab.isFixed;
              return (
                <button
                  key={tab.id}
                  className={`tab-button ${activeTab === tab.id ? 'active' : ''} ${tab.hasContent ? 'has-content' : ''} ${isUnread ? 'unread' : ''} ${tab.isFixed ? 'fixed-tab' : ''} ${tab.isError ? 'error-tab' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.icon}
                  <span>{tab.label}</span>
                  {isUnread && <span className="unread-indicator" />}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="execution-content-scrollable">
          {renderTabContent()}
        </div>
      </div>
    );
  };

  return (
    <div className="execution-modal-overlay">
      <div className="execution-modal" onClick={(e) => e.stopPropagation()}>
        <div className="execution-modal-header">
          <div className="modal-title">
            <div className={`title-icon ${isSavePending ? 'running' : executionResult?.success ? 'success' : executionResult ? 'error' : ''}`}>
              {isSavePending ? (
                <Loader2 size={20} className="animate-spin" />
              ) : executionResult?.success ? (
                <CheckCircle size={20} />
              ) : executionResult ? (
                <XCircle size={20} />
              ) : null}
            </div>
            <div className="title-content">
              <h3>{reportName}</h3>
              <div className="execution-meta">
                {isSavePending ? (
                  <span className="status running">
                    Saving in progress...
                  </span>
                ) : executionResult ? (
                  <>
                    <span className={`status ${executionResult?.success ? 'success' : 'error'}`}>
                      {executionResult?.success ? 'Execution Successful' : 'Execution Failed'}
                    </span>
                    {executionResult && (
                      <>
                        <span className="execution-time">
                          <Clock size={12} />
                          {formatExecutionTime(executionResult.execution_time_seconds)}
                        </span>
                        <span className="return-code">
                          Return code: {executionResult.return_code ?? 'N/A'}
                        </span>
                      </>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </div>
          <div className="modal-actions">
            {selectedWorkspace?.is_owner && onEdit && (
              <button
                className="modal-action-button edit"
                onClick={() => onEdit()}
                title="Edit report"
              >
                <Edit3 size={16} />
              </button>
            )}
            {selectedWorkspace?.is_owner && onDelete && (
              <button
                className="modal-action-button delete"
                onClick={() => onDelete()}
                title={isSavePending ? "Cannot delete while saving" : "Delete report"}
                disabled={isSavePending}
              >
                <Trash2 size={16} />
              </button>
            )}
            <button className="close-button" onClick={onClose}>
              <X size={20} />
            </button>
          </div>
        </div>

        <div className="execution-modal-body">
          {isSavePending && saveLogContent && (
            <div className="output-section">
              <button
                className="section-toggle"
                onClick={() => toggleSection('saveLog')}
              >
                <ChevronDown size={16} className={expandedSections.saveLog ? 'chevron-expanded' : 'chevron-collapsed'} />
                <h4>Save Progress Log</h4>
                <div className="section-toggle-spacer" />
                <div className="content-actions">
                  <button
                    className="action-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleCopy(saveLogContent, 'saveLog');
                    }}
                    title="Copy log"
                  >
                    {copiedStates.saveLog ? <CheckCircle size={14} /> : <Copy size={14} />}
                  </button>
                </div>
              </button>
              {expandedSections.saveLog && (
                <pre className="execution-output save-log">
                  {saveLogContent}
                </pre>
              )}
            </div>
          )}

          {!isSavePending && (
            <div className="execution-runs-panel">
              <div className="runs-panel-header">
                {onRun && (
                  <button
                    className="runs-panel-run-button"
                    onClick={() => handleRunReport()}
                    title="Run report"
                    disabled={isRunning}
                  >
                    {isRunning ? (
                      <Loader2 size={16} className="animate-spin" />
                    ) : (
                      <Play size={16} />
                    )}
                    <span>{isRunning ? 'Running...' : 'Run'}</span>
                  </button>
                )}
                <div className="runs-panel-header-content">
                  <h4>Run History</h4>
                  <span className="runs-count">{runHistory?.length || 0}</span>
                </div>
              </div>
              {runHistory && runHistory.length > 0 && (
                <div className="runs-panel-list">
                  {runHistory.map((run) => {
                    const { timeStr, relativeStr } = formatRunTime(run.timestamp);
                    return (
                      <button
                        key={run.timestamp}
                        className={`run-panel-item ${selectedRunTimestamp === run.timestamp ? 'active' : ''}`}
                        onClick={() => loadHistoricalRun(run.timestamp)}
                      >
                        <span className="run-time">{timeStr}</span>
                        <span className="run-relative">{relativeStr}</span>
                      </button>
                    );
                  })}
                </div>
              )}
              {reportId && (
                <div className="runs-panel-api" ref={apiDropdownRef}>
                  <button
                    className="api-dropdown-trigger"
                    onClick={() => setShowApiDropdown(!showApiDropdown)}
                    title="API Integration"
                  >
                    <Code size={12} />
                    <span>API</span>
                    <ChevronDown size={10} className={showApiDropdown ? 'chevron-expanded' : ''} />
                  </button>
                  {showApiDropdown && (
                    <div className="api-dropdown-menu api-dropdown-menu-up">
                      <div className="api-dropdown-header">
                        <Link size={14} />
                        <span>API Integration</span>
                      </div>
                      <div className="api-dropdown-id">
                        <span className="api-dropdown-id-label">Report ID</span>
                        <div className="api-dropdown-id-row">
                          <code className="api-dropdown-id-value">{reportId}</code>
                          <button
                            className="api-dropdown-id-copy"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCopy(reportId, 'reportId');
                            }}
                            title="Copy Report ID"
                          >
                            {copiedStates.reportId ? <CheckCircle size={12} /> : <Copy size={12} />}
                          </button>
                        </div>
                      </div>
                      <div className="api-dropdown-divider" />
                      <div className="api-dropdown-section-label">Copy API Call</div>
                      <button
                        className="api-dropdown-item"
                        onClick={() => {
                          handleCopy(getCurlCommand(), 'curlCommand');
                          setShowApiDropdown(false);
                        }}
                      >
                        <Terminal size={14} />
                        <span>cURL</span>
                        {copiedStates.curlCommand && <CheckCircle size={12} className="copied-icon" />}
                      </button>
                      <button
                        className="api-dropdown-item"
                        onClick={() => {
                          handleCopy(getPythonCode(), 'pythonCode');
                          setShowApiDropdown(false);
                        }}
                      >
                        <Code size={14} />
                        <span>Python</span>
                        {copiedStates.pythonCode && <CheckCircle size={12} className="copied-icon" />}
                      </button>
                      <button
                        className="api-dropdown-item"
                        onClick={() => {
                          handleCopy(getJavaScriptCode(), 'jsCode');
                          setShowApiDropdown(false);
                        }}
                      >
                        <FileText size={14} />
                        <span>JavaScript</span>
                        {copiedStates.jsCode && <CheckCircle size={12} className="copied-icon" />}
                      </button>
                      <div className="api-dropdown-footer">
                        <span>Replace YOUR_API_KEY with your workspace API key</span>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="execution-main-content">
            <div className="execution-content-wrapper">
              {isSavePending ? (
                <div className="no-outputs">
                  <Loader2 size={32} className="animate-spin" />
                  <p>Saving report...</p>
                  <span style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
                    This may take a moment. Check the log above for details.
                  </span>
                </div>
              ) : (
                renderContent()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ReportExecutionModal;
